// DataTable.tsx
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "contexts/i18nContext";

export interface Column<T> {
  header: string;
  accessor: string;
  render?: (item: T) => React.ReactNode;
  widthClass?: string;
}

export interface DataTableProps<T> {
  title?: string;
  columns: Column<T>[];
  data: T[];
  rowKey: (item: T) => string | number;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  isLoading?: boolean;
  loadingText?: string;
  pagination?: boolean; // enable/disable internal pagination
  defaultPageSize?: number; // initial rows per page
  pageSizeOptions?: Array<number | "all">; // rows per page choices
}

export function DataTable<T>({
  title,
  columns,
  data,
  rowKey,
  searchPlaceholder,
  onSearch,
  isLoading = false,
  loadingText,
  pagination = true,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100, "all"],
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const resolvedSearchPlaceholder = searchPlaceholder || t("search");
  const resolvedLoadingText = loadingText || t("loading");

  // Auto-enable pagination when data length exceeds 6 items
  const shouldShowPagination = pagination && data.length > 6;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | "all">(defaultPageSize);

  // Reset to first page when data set changes or page size changes
  useEffect(() => {
    setPage(1);
  }, [data, pageSize]);

  const total = data.length;
  const pageCount = useMemo(() => {
    if (!shouldShowPagination) return 1;
    if (pageSize === "all") return 1;
    return Math.max(1, Math.ceil(total / (pageSize || 1)));
  }, [shouldShowPagination, pageSize, total]);

  const startIndex = useMemo(() => {
    if (!shouldShowPagination || pageSize === "all") return 0;
    return (page - 1) * (pageSize as number);
  }, [shouldShowPagination, page, pageSize]);

  const endIndex = useMemo(() => {
    if (!shouldShowPagination || pageSize === "all") return total;
    return Math.min(total, startIndex + (pageSize as number));
  }, [shouldShowPagination, pageSize, startIndex, total]);

  const paginatedData = useMemo(() => {
    if (!shouldShowPagination) return data;
    if (pageSize === "all") return data;
    return data.slice(startIndex, endIndex);
  }, [data, shouldShowPagination, pageSize, startIndex, endIndex]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setSearch(q);
    onSearch?.(q);
  };

  return (
    <div className="bg-white rounded-xl w-full shadow overflow-auto">
      {title && (
        <div className="flex items-center justify-between bg-black text-white p-4 rounded-t-xl">
          <h2 className="text-lg font-semibold">{title}</h2>
          <div className="relative w-80">
            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 pointer-events-none">
              🔍
            </span>
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder={resolvedSearchPlaceholder}
              className="w-full pl-10 pr-4 py-2 text-sm text-black placeholder-gray-500 bg-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>
      )}

      <table className="w-full text-sm border-collapse">
        <thead className="bg-black text-white text-left">
          <tr>
            {columns?.map((col) => (
              <th key={col.accessor} className={`p-3 ${col.widthClass || ""}`}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td
                colSpan={columns?.length}
                className="p-8 text-center text-gray-600"
              >
                <div className="flex items-center justify-center gap-3">
                  <div className="h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm font-medium">
                    {resolvedLoadingText}
                  </span>
                </div>
              </td>
            </tr>
          ) : paginatedData.length > 0 ? (
            paginatedData.map((row, idx) => (
              <tr
                key={rowKey(row)}
                className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                {columns?.map((col) => (
                  <td
                    key={col.accessor}
                    className={`p-3 ${col.widthClass || ""}`}
                  >
                    {col.render ? col.render(row) : (row as any)[col.accessor]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns?.length}
                className="p-4 text-center text-gray-500"
              >
                {t("noDataAvailable")}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {!isLoading && shouldShowPagination && total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 border-t">
          {/* Rows per page selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 min-w-[100px]">
              {t("rowsPerPage")}
            </span>
            <select
              className="border rounded px-2 py-1 text-sm bg-white"
              value={pageSize as any}
              onChange={(e) => {
                const val = e.target.value;
                setPageSize(val === "all" ? "all" : Number(val));
              }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={String(opt)} value={opt as any}>
                  {opt === "all" ? t("all") : opt}
                </option>
              ))}
            </select>
          </div>

          {/* Page info */}
          <div className="text-sm text-gray-600">
            {total === 0
              ? `0 ${t("of")} 0`
              : `${pageSize === "all" ? 1 : startIndex + 1}-${
                  pageSize === "all" ? total : endIndex
                } ${t("of")} ${total}`}
          </div>

          {/* Pagination controls */}
          <div className="flex items-center gap-2">
            <button
              className="px-2 py-1 text-sm border rounded disabled:opacity-50"
              onClick={() => setPage(1)}
              disabled={page <= 1 || pageSize === "all"}
            >
              « {t("first")}
            </button>
            <button
              className="px-2 py-1 text-sm border rounded disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || pageSize === "all"}
            >
              ‹ {t("prev")}
            </button>
            <span className="text-sm text-gray-700">
              {t("page")} {page}{" "}
              {pageSize !== "all" && `${t("of")} ${pageCount}`}
            </span>
            <button
              className="px-2 py-1 text-sm border rounded disabled:opacity-50"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page >= pageCount || pageSize === "all"}
            >
              {t("next")} ›
            </button>
            <button
              className="px-2 py-1 text-sm border rounded disabled:opacity-50"
              onClick={() => setPage(pageCount)}
              disabled={page >= pageCount || pageSize === "all"}
            >
              {t("last")} »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
